import { fork } from '@cortex/utils/fork';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import { PageHeader } from '../../../components/common/PageHeader';
import { Table, type IColumn } from '../../../components/common/Table';
import { useMcpActivityEffect } from '../../../hooks/useMcpActivity';
import { routes } from '../../../lib/constants';
import { formatRelative } from '../../../lib/format';
import { useMetaphysics, type IBlueprint } from '../../../lib/metaphysics';
import { guardSuperuser } from '../index';

const blueprintMutationTools = new Set([
  'create_blueprint',
  'update_blueprint',
  'delete_blueprint',
]);

export const Route = createFileRoute('/internal/blueprints/')({
  beforeLoad: () => guardSuperuser(),
  component: BlueprintsListPage,
});

function BlueprintsListPage() {
  const mp = useMetaphysics();
  const nav = useNavigate();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['mp', 'blueprints'],
    queryFn: () => mp.listBlueprints(),
  });

  // Refetch the list when the chat agent runs any blueprint-mutating MCP
  // tool through the floating chat. We fire on the result phase so we
  // don't refetch while the call is still in flight.
  useMcpActivityEffect(
    (e) => e.status === 'ok' && blueprintMutationTools.has(e.name),
    () => {
      fork(qc.invalidateQueries({ queryKey: ['mp', 'blueprints'] }));
    },
  );

  const cols: IColumn<IBlueprint>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (b) => (
        <Link
          to={routes.internalBlueprintDetail(b.id)}
          style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}
        >
          {b.name}
        </Link>
      ),
    },
    {
      key: 'prompts',
      header: 'Prompts',
      width: 100,
      cell: (b) => <span className="badge badge--neutral">{b.prompts?.length ?? 0}</span>,
    },
    {
      key: 'targets',
      header: 'Target artifacts',
      width: 160,
      cell: (b) => <span className="badge badge--neutral">{b.targetArtifacts?.length ?? 0}</span>,
    },
    {
      key: 'skills',
      header: 'Skills repo',
      cell: (b) =>
        b.skillsRepoUrl ? (
          <code className="t-mono t-secondary">{b.skillsRepoUrl}</code>
        ) : (
          <span className="t-muted">—</span>
        ),
    },
    {
      key: 'created',
      header: 'Created',
      mono: true,
      width: 140,
      cell: (b) => (b.createdAt ? formatRelative(b.createdAt) : <span className="t-muted">—</span>),
    },
  ];

  return (
    <>
      <PageHeader
        title="Blueprints"
        actions={
          <Link to={routes.INTERNAL_BLUEPRINTS_NEW} className="btn btn--primary btn--sm">
            New blueprint
          </Link>
        }
      />
      {list.error ? (
        <div className="alert alert--danger" style={{ marginBottom: 'var(--sp-3)' }}>
          {(list.error as Error).message}
        </div>
      ) : null}
      <Table<IBlueprint>
        rows={list.data}
        columns={cols}
        rowKey={(b) => b.id}
        isLoading={list.isLoading}
        error={list.error as Error | null}
        emptyTitle="No blueprints yet"
        emptyDescription="A blueprint is a named set of prompts and target artifacts. Pipelines compose them at run time."
        emptyCta={
          <Link to={routes.INTERNAL_BLUEPRINTS_NEW} className="btn btn--primary btn--sm">
            New blueprint
          </Link>
        }
        onRowClick={async (b) => {
          try {
            await nav({ to: routes.internalBlueprintDetail(b.id) });
          } catch (err) {
            console.error(err);
          }
        }}
      />
    </>
  );
}
